import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import type { ForteXApi } from "@fortex/api";
import type { MapCoreApi } from "../core/api";
import { createMapCore } from "../core/MapCore";
import type { ForteXMapConfig } from "../core/options";
import { resolveOptions } from "../core/options";
import { CoreContext } from "./coreContext";
import { catalogueEntryOf } from "../catalogue/types";
import { LayoutContext, useContainerSize, useForteXMapHandle, useMapStore } from "./hooks";
import type { ContainerSize } from "./hooks";
import { DefaultChrome } from "./components/DefaultChrome";
import { MapCanvas } from "./components/MapCanvas";

export type ForteXMapProps = {
  api: ForteXApi;
  config?: ForteXMapConfig;
  onReady?: (core: MapCoreApi) => void;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

export function useForteXMap(): ReturnType<typeof useForteXMapHandle> {
  return useForteXMapHandle();
}

function RootAttributes({ rootRef }: { rootRef: RefObject<HTMLDivElement | null> }) {
  const theme = useMapStore(a => a.theme);
  const dark = useMapStore(a => a.dark);
  const layers = useMapStore(a => a.layers);
  const catalogue = useMapStore(a => a.catalogue);
  const baseDark = layers.some(a => a.root === "maps" && a.selected && (catalogueEntryOf(catalogue, a)?.tile?.dark || catalogueEntryOf(catalogue, a)?.special?.dark));

  useEffect(() => {
    const root = rootRef.current;

    if (root === null) return;

    root.dataset.theme = theme;
    root.classList.toggle("fxmap-dark", dark);
    root.dataset.basemap = baseDark ? "dark" : "light";
  }, [rootRef, theme, dark, baseDark]);

  return null;
}

export type MapCoreProviderProps = { core: MapCoreApi; size?: ContainerSize; children: ReactNode };

export function MapCoreProvider({ core, size, children }: MapCoreProviderProps) {
  const content = size === undefined ? children : <LayoutContext.Provider value={size}>{children}</LayoutContext.Provider>;

  return <CoreContext.Provider value={core}>{content}</CoreContext.Provider>;
}

export function ForteXMap({ api, config, onReady, children, className, style }: ForteXMapProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const configRef = useRef(config);
  const readyRef = useRef(onReady);
  const [core, setCore] = useState<MapCoreApi | null>(null);
  const size = useContainerSize(rootRef);
  const options = resolveOptions(config);

  useEffect(() => {
    configRef.current = config;
    readyRef.current = onReady;
  });

  useEffect(() => {
    const canvas = canvasRef.current;

    if (canvas === null) return;

    const instance = createMapCore(canvas, api, configRef.current ?? {});

    setCore(instance);
    readyRef.current?.(instance);

    return () => {
      setCore(null);
      instance.destroy();
    };
  }, [api]);

  return (
    <div ref={rootRef} className={["fxmap", className ?? ""].filter(Boolean).join(" ")} style={style} data-fxmap="root" data-theme={options.theme} data-density={options.density} data-chrome={options.chrome} data-breakpoint={size.breakpoint}>
      <MapCanvas ref={canvasRef} />
      {core !== null ? (
        <MapCoreProvider core={core} size={size}>
          <RootAttributes rootRef={rootRef} />
          {children ?? <DefaultChrome />}
        </MapCoreProvider>
      ) : null}
    </div>
  );
}
